/**
 * Accuracy Dashboard Constants
 *
 * Centralizes thresholds and configuration for data quality indicators
 */

/**
 * Minimum sample size threshold for reliable metrics
 * Below this threshold, show "(Limited data)" warnings
 */
export const SMALL_SAMPLE_THRESHOLD = 10;

/**
 * Data quality levels based on sample size
 */
export const DATA_QUALITY = {
  INSUFFICIENT: 0,      // No data
  MINIMAL: 1,           // 1-4 samples (very limited)
  LIMITED: 5,           // 5-9 samples (limited but usable)
  ADEQUATE: 10,         // 10+ samples (statistically meaningful)
  ROBUST: 30,           // 30+ samples (robust)
} as const;

/**
 * Get data quality level for a given sample size
 */
export function getDataQuality(sampleSize: number): keyof typeof DATA_QUALITY {
  if (sampleSize === 0) return 'INSUFFICIENT';
  if (sampleSize < DATA_QUALITY.LIMITED) return 'MINIMAL';
  if (sampleSize < DATA_QUALITY.ADEQUATE) return 'LIMITED';
  if (sampleSize < DATA_QUALITY.ROBUST) return 'ADEQUATE';
  return 'ROBUST';
}

/**
 * Get user-friendly label for data quality
 */
export function getDataQualityLabel(sampleSize: number): string {
  const quality = getDataQuality(sampleSize);

  switch (quality) {
    case 'INSUFFICIENT': return 'No data';
    case 'MINIMAL': return 'Very limited data';
    case 'LIMITED': return 'Limited data';
    case 'ADEQUATE': return '';  // No warning needed
    case 'ROBUST': return '';     // No warning needed
    default: return '';
  }
}

/**
 * Check if sample size is below reliable threshold
 */
export function isSmallSample(sampleSize: number): boolean {
  return sampleSize < SMALL_SAMPLE_THRESHOLD && sampleSize > 0;
}
